import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import {
  DiscoveryParametersDeserialized,
  isUnifiedDiscoveryPreset,
} from '../../utils/discovery/types';
import { serializeParams } from '../../utils/discovery/utils';

const MAX_PAGE_OFFSET = 20;

type Return = { availablePages: number; hasMore: boolean };

interface Params extends DiscoveryParametersDeserialized {
  highestKnownPage: number;
  maxPageOffset?: number;
  totalCount?: number;
}

/**
 * This hook will progressively discover the available pages for a given set of parameters.
 * This is required, as we don't know ahead of time how many pages are available, but want to allow users to paginate to specific pages.
 * This hook will use binary search to discover the highest page number that is available out of the provided range.
 */
export function useDiscoverAvailablePages({
  page,
  sortBy,
  sortDirection,
  timeframe,
  filters,
  preset,
  highestKnownPage,
  maxPageOffset = MAX_PAGE_OFFSET,
  totalCount,
  searchQuery,
}: Params) {
  const queryClient = useQueryClient();
  const pageSize = 50;
  const unifiedAvailablePages =
    isUnifiedDiscoveryPreset(preset) && typeof totalCount === 'number'
      ? Math.ceil(totalCount / pageSize)
      : undefined;

  return useQuery<Return>({
    queryKey: [
      'discover-available-pages',
      page,
      sortBy,
      sortDirection,
      timeframe,
      filters,
      preset,
      totalCount,
      searchQuery ?? '',
    ],
    enabled: !isUnifiedDiscoveryPreset(preset),
    initialData:
      unifiedAvailablePages !== undefined
        ? { availablePages: unifiedAvailablePages, hasMore: false }
        : undefined,
    staleTime: 2 * 60 * 1000,
    placeholderData: (previousData, previousQuery) => {
      const previousQueryKey = previousQuery?.queryKey;
      if (!previousQueryKey) {
        return undefined;
      }
      const [
        ,
        ,
        previousSortBy,
        previousSortDirection,
        previousTimeframe,
        previousFilters,
        previousPreset,
        previousTotalCount,
        previousSearchQuery,
      ] = previousQueryKey;
      if (
        previousSortBy !== sortBy ||
        previousSortDirection !== sortDirection ||
        previousTimeframe !== timeframe ||
        !isEqual(previousFilters, filters) ||
        previousPreset !== preset ||
        previousTotalCount !== totalCount ||
        previousSearchQuery !== (searchQuery ?? '')
      ) {
        return undefined;
      }
      return previousData;
    },
    queryFn: async ({ signal }) => {
      const coreFilters: ReturnType<typeof serializeParams> = serializeParams({
        page,
        sortBy,
        sortDirection,
        filters,
        timeframe,
        preset,
        pageDiscovery: true,
        searchQuery,
      });

      // binary search for the highest page that is available
      // When highestKnownPage is 0 (after filter reset), we need to check from page 1
      let lo = highestKnownPage === 0 ? 1 : Math.max(page, highestKnownPage);
      let hi = page + maxPageOffset;
      let highestAvailable = 0;

      while (lo <= hi) {
        const url = new URL('/api/discovery/tokens', window.location.origin);
        const mid = lo + Math.floor((hi - lo) / 2);
        coreFilters.page = mid;
        const params = new URLSearchParams();
        Object.entries(coreFilters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            params.set(key, value.join(','));
          } else {
            if (typeof value === 'number') {
              params.set(key, value.toString());
            } else if (typeof value === 'string') {
              params.set(key, value);
            }
          }
        });
        url.search = params.toString();

        const response = await fetch(url.toString(), { signal });
        if (response.ok) {
          const result = await response.json();
          // Check if the response has data (not null)
          if (result.type === 'success' && result.data !== null) {
            highestAvailable = mid;
            lo = mid + 1;
          } else {
            // No data found, adjust the search range
            hi = mid - 1;
          }
        } else {
          hi = mid - 1;
        }
      }

      const availablePages = highestAvailable;

      const returnValue = { availablePages, hasMore: highestAvailable >= page + maxPageOffset };

      // update the cache of all previous pages
      for (let i = 1; i < page; i++) {
        queryClient.setQueryData(
          [
            'discover-available-pages',
            i,
            sortBy,
            sortDirection,
            timeframe,
            filters,
            preset,
            totalCount,
            searchQuery ?? '',
          ],
          returnValue,
        );
      }
      return returnValue;
    },
  });
}
